import React, { useState, useEffect } from 'react';
import './StockHoldings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'x-auth-token': token } : {};
};

function StockHoldings() {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/stocks`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch stock data');
      const data = await response.json();
      setStockData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
    const interval = setInterval(fetchStocks, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatNumber = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatShares = (shares) => {
    if (shares >= 1) {
      return shares.toLocaleString('en-US', { maximumFractionDigits: 2 });
    }
    return shares.toLocaleString('en-US', { maximumFractionDigits: 4 });
  };

  if (loading) {
    return (
      <div className="stock-holdings loading">
        <div className="stock-header">
          <h2>Stocks</h2>
        </div>
        <div className="stock-loading">fetching prices...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stock-holdings error">
        <div className="stock-header">
          <h2>Stocks</h2>
        </div>
        <div className="stock-error">
          <p>error: connection failed</p>
          <button onClick={fetchStocks}>retry</button>
        </div>
      </div>
    );
  }

  if (!stockData || stockData.holdings.length === 0) {
    return (
      <div className="stock-holdings empty">
        <div className="stock-header">
          <h2>Stocks</h2>
        </div>
        <div className="stock-empty">
          <p>no holdings found</p>
          <p className="stock-hint">add OfxDownload.csv</p>
        </div>
      </div>
    );
  }

  const sortedHoldings = [...stockData.holdings].sort((a, b) => b.value - a.value);

  return (
    <div className="stock-holdings">
      <div className="stock-header">
        <h2>Stocks</h2>
        <div className="stock-total">
          <span className="total-value">${formatNumber(stockData.totalValue)}</span>
          {stockData.change !== 0 && (
            <span className={`total-change ${stockData.change >= 0 ? 'positive' : 'negative'}`}>
              {stockData.change >= 0 ? '+' : ''}{stockData.change.toFixed(2)}%
            </span>
          )}
        </div>
      </div>

      <div className="stock-list">
        {sortedHoldings.map(holding => (
          <div key={holding.symbol} className="stock-item">
            <div className="stock-info">
              <span className="stock-symbol">{holding.symbol}</span>
              {holding.change !== 0 && (
                <span className={`stock-change ${holding.change >= 0 ? 'positive' : 'negative'}`}>
                  {holding.change >= 0 ? '+' : ''}{holding.change.toFixed(2)}%
                </span>
              )}
            </div>
            <div className="stock-shares">
              <span className="shares-count">{formatShares(holding.shares)}</span>
            </div>
            <div className="stock-price">
              <span className="price-current">${formatNumber(holding.price)}</span>
            </div>
            <div className="stock-value">
              ${formatNumber(holding.value)}
            </div>
          </div>
        ))}
      </div>

      {stockData.lastUpdated && (
        <div className="stock-footer">
          {new Date(stockData.lastUpdated).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

export default StockHoldings;
